import { useSuspenseQuery, useMutation } from '@apollo/client/react';
import { useForm } from '@tanstack/react-form';
import { ReactElement, useEffect, useState, useTransition } from 'react';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import {
  GQL_QUERY_READ_SOURCE,
  GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
  GQL_MUTATION_UPDATE_SOURCE,
  GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS,
  GQL_QUERY_INDEX_SOURCES,
  GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
  GQL_FRAGMENT_PROJECT,
} from '../utility/queries';
import { ProjectState, SourceType } from '@sc/graphql';
import {
  FileToUploadInputType,
  FileUploadProgress,
  Input,
  Loader,
  supportedAIProcessingMimeTypes,
  TablePagination,
  UploadButton,
} from '@sc/shared-ui';
import { SourceDocumentCard } from '../components/SourceDocumentCard';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { track } from '../utility/analytics';

interface Props {
  projectUuid: string;
  onUploadingChange?: ((isUploading: boolean) => void) | undefined;
}

export const File = ({ projectUuid, onUploadingChange }: Props): ReactElement => {
  const { data: source } = useSuspenseQuery(GQL_QUERY_READ_SOURCE, {
    variables: {
      input: {
        type: SourceType.file,
        projectUuid,
      },
    },
  });

  const [page, setPage] = useState(1);

  const [, startTransition] = useTransition();

  const { data: sourceDocuments } = useSuspenseQuery(
    GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
    {
      variables: {
        dimensionsInput: undefined,
        input: {
          filter: {
            sourceUuid: source.readSource.uuid,
          },
          pagination: {
            perPage: 20,
            page,
          },
        },
      },
    }
  );

  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const [uploading, setUploading] = useState<FileToUploadInputType[]>([]);

  const isUploading = uploading.length > 0;

  // Surface in-flight uploads so the surrounding modal can block closing.
  useEffect(() => {
    onUploadingChange?.(isUploading);

    return () => onUploadingChange?.(false);
  }, [isUploading, onUploadingChange]);

  const [updateSource] = useMutation(GQL_MUTATION_UPDATE_SOURCE, {
    onError,
    onCompleted: () => notify.success('Saved source name.'),
  });

  const form = useForm({
    defaultValues: {
      name: source.readSource.name ?? '',
    },
    onSubmit: ({ value }) => {
      if (value.name && value.name !== source.readSource.name) {
        void updateSource({
          variables: {
            input: {
              uuid: source.readSource.uuid,
              name: value.name,
            },
          },
        });
      }
    },
  });

  const [save, { loading }] = useMutation(
    GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS,
    {
      onError,
      refetchQueries: [
        GQL_QUERY_INDEX_SOURCES,
        GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
        GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
      ],
      update: (cache) =>
        cache.updateFragment(
          {
            id: `Project:${projectUuid}`,
            fragment: GQL_FRAGMENT_PROJECT,
          },
          (d) =>
            d
              ? {
                  ...d,
                  state: ProjectState.draft,
                  processedPc: 0,
                  sourceCount: d.sourceCount + 1,
                }
              : d
        ),
      onCompleted: () => {
        notify.success('Saved source.');
      },
    }
  );

  return (
    <div className="overflow-y-auto flex flex-col p-5">
      <form.Field
        name="name"
        children={({ state, handleChange }) => (
          <Input
            label="Source name (optional)"
            onBlur={() => void form.handleSubmit()}
            value={state.value}
            inputClassname="h-10!"
            onChange={(e) => handleChange(e.target.value)}
          />
        )}
      />
      <div className="mt-5 flex flex-col space-y-5">
        <UploadButton
          uploading={uploading}
          setUploading={setUploading}
          onFilesUpload={(files) =>
            void save({
              variables: {
                input: {
                  uuid: source.readSource.uuid,
                  documentsToCreate: files.map((f) => ({
                    fileUuid: f.uuid,
                    relevance: new Date().toISOString(),
                    description: String(f.size),
                    title: f.name,
                    hostedIntegrationDocumentId: undefined,
                    hostedIntegrationDocumentPath: undefined,
                    url: undefined,
                    thumbnailUuid: undefined,
                    hostedIntegrationMimeType: f.mimeType
                  })),
                  documentsToDelete: [],
                  integrationType: undefined
                },
              },
            }).then(({ data }) => {
              if (!data) return;

              track('Source Documents Updated', {
                sourceType: SourceType.file,
                projectUuid,
                documentsCreated: files.length,
                documentsDeleted: 0,
              });
            })
          }
          keyPrefix={`projects/${projectUuid}/sources`}
          projectUuid={projectUuid}
          asDropzone
          multiple
          resolveMimeType={(type) => type}
          accept={supportedAIProcessingMimeTypes.chunkableTextMimeTypes.join(
            ','
          )}
          fileLimit={500}
        />
        <FileUploadProgress uploading={uploading} />
      </div>
      {loading && (
        <div className="py-5 m-auto">
          <Loader className="size-6" />
        </div>
      )}
      <div className="py-5 space-y-2 pb-5">
        {sourceDocuments.indexSourceDocuments.items.map((sd) => (
          <SourceDocumentCard
            sd={sd}
            sourceUuid={source.readSource.uuid}
            sourceType={SourceType.file}
            getPath={() => sd.title}
            FallbackIcon={DocumentIcon}
          />
        ))}
        {sourceDocuments.indexSourceDocuments.pagination.lastPage > 1 && (
          <TablePagination
            page={page}
            totalPages={sourceDocuments.indexSourceDocuments.pagination.lastPage}
            setPage={(next) => startTransition(() => setPage(next))}
          />
        )}
      </div>
    </div>
  );
};
